import { cn } from '@/lib/utils';
import Combobox from '@/libs/components/src/lib/form/Combobox';
import { Tooltip } from '@/libs/components/src/lib/Tooltip';
import { ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import { useState } from 'react';
import Modal from '../../../../common/Modal';
import InlineAssessmentInput from './InlineAssessmentInput';
import NeuroDropdown from './NeuroDropdown';

const cranialRows = [
  {
    key: 'CNI',
    text: 'CN I',
    default: 'Smell intact',
    type: 'dropdown',
    arr: ['Smell intact', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'CNIIIIIIVVI',
    text: 'CN II, III, IV, VI',
    default: [
      'Visual acuity intact',
      'Visual fields normal',
      'Pupils equal/round/reactive/accomodate',
      'Extraocular movements intact',
      'No Nystagmus',
      'No Ptosis',
    ],
    type: 'multiselect',
    arr: [
      'Visual acuity intact',
      'Visual fields normal',
      'Pupils equal/round/reactive/ accommodate',
      'Extraocular movements intact',
      'No Nystagmus',
      'No Ptosis',
      'Abnormal',
      'Not assessed',
    ],
  },
  {
    key: 'CNV',
    text: 'CN V',
    default: 'Face sensation intact',
    type: 'dropdown',
    arr: ['Face sensation intact', 'Abnormal', 'Not assessed'],
  },

  {
    key: 'CNVII',
    text: 'CN VII',
    default: 'Face motor intact',
    type: 'dropdown',
    arr: ['Face motor intact', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'CNVIII',
    text: 'CN VIII',
    default: 'Hearing intact',
    type: 'dropdown',
    arr: ['Hearing intact', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'CNIXX',
    text: 'CN IX, X',
    default: ['Swallow and gag reflex intact', 'Speech intact', 'Voice normal'],
    type: 'multiselect',
    arr: [
      'Swallow and gag reflex intact',
      'Speech intact',
      'Voice normal',
      'Abnormal',
      'Not assessed',
    ],
  },
  {
    key: 'CNXI',
    text: 'CN XI',
    default: 'Head turn/shoulder shrug intact',
    type: 'dropdown',
    arr: ['Head turn/shoulder shrug intact', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'CNXII',
    text: 'CN XII',
    default: 'Tongue protrusion midline',
    type: 'dropdown',
    arr: ['Tongue protrusion midline', 'Abnormal', 'Not assessed'],
  },
];

type Props = {
  isOpen: boolean;
  close: () => void;
  value: any;
  onChange: (value?: any) => void;
  signed?: boolean;
};

const CranialEditableChart: React.FC<Props> = ({ isOpen, close, value, onChange, signed }) => {
  const [confirmOpen, setConfirmOpen] = useState<OpenClosedStates>(OpenClosedStates.Closed);
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [editingExplanation, setEditingExplanation] = useState<string>('');

  const clearValues = () => {
    onChange({ result: value?.result });
  };

  const getOverallResult = (val: string | string[], key: string) => {
    const isAbnormal = Array.isArray(val) ? val?.includes?.('Abnormal') : val === 'Abnormal';

    if (isAbnormal) {
      return 'abnormal';
    }

    for (const row of cranialRows) {
      const choice = value?.detail?.[row.key]?.choice;

      if (
        row.key !== key &&
        ((typeof choice === 'string' && choice === 'Abnormal') || choice?.includes('Abnormal'))
      ) {
        return 'abnormal';
      }
    }

    return 'normal';
  };

  const getResult = (val: string | string[]) => {
    const isAbnormal = Array.isArray(val) ? val?.includes?.('Abnormal') : val === 'Abnormal';

    if (isAbnormal) {
      return 'abnormal';
    }

    return 'normal';
  };

  const onChangeItem = (key: string) => (selectedValue: string | string[]) => {
    if (signed) return;
    let newValue = selectedValue;

    if (Array.isArray(selectedValue)) {
      // if "Abnormal" is currently selected and another value is chosen, replace "Abnormal" with the new value
      if (selectedValue.includes('Abnormal') && selectedValue.length > 1) {
        // remove "Abnormal" and keep only the newly selected value (the last one)
        newValue = [selectedValue[selectedValue.length - 1]];
      } else if (selectedValue.includes('Abnormal')) {
        newValue = ['Abnormal'];
      } else if (selectedValue.includes('Not assessed')) {
        newValue = [];
      }
    }

    const overallResult = getOverallResult(newValue, key);
    const result = getResult(newValue);
    const isAbnormal = result === 'abnormal';

    onChange({
      ...value,
      detail: {
        ...value?.detail,
        [key]: {
          ...value?.detail?.[key],
          choice: newValue,
        },
      },
      result: overallResult,
    });

    if (isAbnormal) {
      setEditingRowKey(key);
      setEditingExplanation(value?.detail?.[key]?.explanation || '');
    } else if (editingRowKey === key) {
      setEditingRowKey(null);
      setEditingExplanation('');
    }
  };

  const handleExplanationChange = (val: string) => {
    setEditingExplanation(val);
  };

  const handleExplanationSubmit = (rowKey: string) => {
    onChange({
      ...value,
      detail: {
        ...value?.detail,
        [rowKey]: {
          ...value?.detail?.[rowKey],
          explanation: editingExplanation,
        },
      },
    });
    setEditingRowKey(null);
    setEditingExplanation('');
  };

  const checkOverall = () => {
    for (const row of cranialRows) {
      const choice = value?.detail?.[row.key]?.choice;

      if ((typeof choice === 'string' && choice === 'Abnormal') || choice?.includes?.('Abnormal')) {
        return 'abnormal';
      }
    }

    return 'normal';
  };

  const closeModal = () => {
    const overAll = checkOverall();

    if (value?.result === overAll || signed) {
      close();

      return;
    }

    setConfirmOpen(OpenClosedStates.Open);
  };

  const confirm = () => {
    setConfirmOpen(OpenClosedStates.Closed);
    onChange({
      ...value,
      result: 'normal',
    });
    close();
  };

  return (
    <Modal
      isOpen={isOpen}
      close={closeModal}
      size="3xl"
      header={{
        title: 'Cranial Neurologic Test',
      }}
      footer={{
        buttons: [
          {
            text: 'Clear All Values',
            onClick: clearValues,
          },
          {
            text: 'Close',
            onClick: closeModal,
            color: ButtonColors.primary,
          },
        ],
      }}
    >
      <div className="w-full">
        <table className="min-w-[380px] overflow-x-auto divide-y divide-gray-300 w-full border">
          <thead className=" dark:bg-dark-gray-900">
            <tr className="dark:bg-dark-600 ">
              <th className="w-1/3 text-center pt-2 pb-2 border-r">Cranial Nerves</th>
              <th className="w-2/3 text-center">Results</th>
            </tr>
            <tr className="dark:bg-dark-600">
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-300 bg-white dark:bg-dark-gray-900 border">
            {cranialRows?.map(cranialRow => (
              <tr key={`${cranialRow.text}`}>
                <th className="dark:bg-dark-gray-900 pl-1 pr-1 border-r pt-2 pb-2">
                  {cranialRow.text}
                </th>
                <td className="text-center">
                  {/* render dropdown or multiselect using appropriate component */}
                  {cranialRow.type === 'dropdown' ? (
                    <div className="flex flex-row items-center justify-center">
                      <NeuroDropdown
                        dropdownOptions={cranialRow.arr}
                        value={value?.detail?.[cranialRow.key] || { choice: '' }}
                        onChange={onChangeItem(cranialRow.key)}
                        defaultVal={cranialRow.default}
                        row={cranialRow.key}
                      />
                      {editingRowKey === cranialRow.key && (
                        <InlineAssessmentInput
                          value={editingExplanation}
                          onChange={handleExplanationChange}
                          onSubmit={() => handleExplanationSubmit(cranialRow.key)}
                          placeholder="Enter abnormal explanation"
                        />
                      )}
                    </div>
                  ) : cranialRow.type === 'multiselect' ? (
                    <div className="flex flex-col items-center justify-center pt-2">
                      {value?.detail?.[cranialRow.key]?.choice.map((item: string) => (
                        <div key={item} className="flex flex-row items-center justify-center gap-2">
                          <span
                            className={cn(
                              item === 'Abnormal' ? 'text-red-500' : 'text-primary-600',
                              'flex flex-row items-center justify-center gap-2'
                            )}
                          >
                            {item}

                            {item === 'Abnormal' &&
                              !!value?.detail?.[cranialRow.key]?.explanation && (
                                <span className="flex justify-center -mt-1">
                                  <Tooltip content={value?.detail?.[cranialRow.key]?.explanation}>
                                    <svg
                                      className="shrink-0 mr-1.5 h-5 w-5 text-gray-400 hover:text-gray-500"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                      id={cranialRow.key}
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </Tooltip>
                                </span>
                              )}

                            {editingRowKey === cranialRow.key && (
                              <InlineAssessmentInput
                                value={editingExplanation}
                                onChange={handleExplanationChange}
                                onSubmit={() => handleExplanationSubmit(cranialRow.key)}
                                placeholder="Enter abnormal explanation"
                              />
                            )}
                          </span>
                        </div>
                      ))}

                      <Combobox
                        name={cranialRow.key}
                        className="mt-1 w-full"
                        options={cranialRow.arr.map(option => ({ value: option, text: option }))}
                        value={
                          Array.isArray(value?.detail?.[cranialRow.key]?.choice)
                            ? value.detail[cranialRow.key].choice
                            : []
                        }
                        onChange={(selectedValues: string[]) =>
                          onChangeItem(cranialRow.key)(selectedValues)
                        }
                        disabled={signed}
                        placeholder="Select results"
                      />
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ConfirmModal
          isOpen={confirmOpen === OpenClosedStates.Open}
          confirm={confirm}
          close={() => setConfirmOpen(OpenClosedStates.Closed)}
          confirmText="Confirm"
          description="No individual tests have been marked abnormal. The overall test result will be changed back to normal."
        />
      </div>
    </Modal>
  );
};

export default CranialEditableChart;
